
import { Enums, DtoKeys } from '@bode-canada/core';
import { ToastMessage } from '@/ui-kit/components/toast/enums';
import ModalService from '@/ui-kit/components/modal/ModalService';
import LoadingSpinner from '@/components/LoadingSpinner';
import Button from '@/components/button';
import FilterChartType from '@/components/filters/chart-type';
import {
  DEFAULT_DATE_PERIOD,
} from '@/components/features/sold-data/shared/components/forms/date-period/constants';
import FilterTime from '@/components/filters/time';
import ToastService from '@/components/toast/toast-service';
import { track } from '@/utils/analytics';
import { PROVINCES } from '@/constants';
import MarketDataApiService from '@/services/api/serverless/market-data';
import DashboardApiService from '@/services/api/serverless/dashboard';
import { CHART_SOLD_YEARS } from '@/constants/MarketResearchConstants';
import { MarketDataEvent } from '@/constants/AnalyticEvents';
import MarketDataShareModal from './share/modal';
import FilterMapper from './mappers/filters';
import { CHART_LABEL } from './constants';
import ChartsFactory from './charts';

/**
 * MarketDataSummaryChart component
 * @deprecated Use @/components/features/market-data/components/chart/MarketDataChart instead
 * TODO: move MarketDataChart component to shared folder
 */
export default {
  name: 'MarketDataSummaryChart',
  components: {
    apexcharts: () => (process.client
      ? import('vue-apexcharts')
      : null),
    LoadingSpinner,
    FilterChartType,
    FilterTime,
    Button,
  },
  props: {
    showFilterButtons: {
      type: Boolean,
      default: false,
    },
    chartType: {
      type: String,
      default: 'Average Sold Price',
    },
    summaryParams: {
      type: Object,
      default: null,
    },
    user: {
      type: Object,
      default: () => null,
    },
    province: {
      type: String,
      default: PROVINCES.AB,
    },
    isInteriorBcSelected: {
      type: Boolean,
      default: false,
    },
    isShowSoldWeek: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chartWidth: '100%',
      chartHeight: '400px',
      chartParams: null,
      chartData: null,
      chartSeries: null,
      chartTypeValue: this.chartType,
      exportChart: null,
      loadingGraph: false,
      transactionDate: '',
    };
  },
  computed: {
    chartTypes() {
      const items = Object.values(CHART_LABEL);
      // Remove 'Average Sold Price per Sqft' option
      // if the selected city is interior BC city
      if (this.isInteriorBcSelected) items.splice(1, 1);
      return items;
    },
    yearItems() {
      const years = [...CHART_SOLD_YEARS];
      // Interior BC -> 10 years
      // REBGV -> 2 years
      if (this.province === PROVINCES.BC && !this.isInteriorBcSelected) {
        years.splice(4);
      }
      return years;
    },
  },
  watch: {
    summaryParams: {
      immediate: true,
      async handler(val) {
        // NOTE: charts are only rendered on client, so fetch only on client
        if (process.client && val) {
          this.chartParams = {
            ...val,
            transactionDate: val.transactionDate || DEFAULT_DATE_PERIOD,
          };
          this.transactionDate = this.chartParams.transactionDate;

          await this.getChartData(this.chartParams, this.chartTypeValue);
        }
      },
    },
  },
  methods: {
    async openShareSearchModal() {
      const shareSearchModal = await ModalService.open({
        component: MarketDataShareModal,
      });

      if (shareSearchModal) {
        ToastService.openPositive(ToastMessage.MarketDataShareSearchSuccess);
        track(MarketDataEvent.ShareSearchSendClick);
      }
    },
    /**
     * This function is called when the component first loads and
     * every time after that when the parent updates it's filters
     */
    async getNewGraph() {
      this.chartParams.transactionDate = this.transactionDate;
      await this.getChartData(this.chartParams, this.chartTypeValue);
      this.wireHamburgerMenuButton();
    },

    /**
     * @track Wires the hamburger button to a click event handler,
     * which in turn wires the "download CSV" button to a click event hanlder,
     * which in turn tracks the clicking of the "download CSV" button in Mixpanel (via Segment).
     *
     * @returns {void}
     * @private
     */
    wireHamburgerMenuButton() {
      const hamburgerMenuButton = document.querySelector('.apexcharts-menu-icon');
      if (hamburgerMenuButton) {
        hamburgerMenuButton.addEventListener('click', () => {
          const exportCSVButton = document.querySelector('.exportCSV');
          if (exportCSVButton) {
            exportCSVButton.addEventListener('click', () => {
              track('Market Data_Download CSV button clicked');
            });
          }
        });
      }
    },
    onChangeChartTypeValue(value) {
      this.chartTypeValue = value;
    },
    onChangeTransactionDateValue(value) {
      this.transactionDate = value;
    },

    handleGenerateGraphClick() {
      track(MarketDataEvent.GenerateGraphClick, {
        chartType: this.chartTypeValue,
        timeRange: this.transactionDate,
      });

      this.getNewGraph();
    },

    async getChartData(params, chartName) {
      this.loadingGraph = true;

      const chartData = chartName === CHART_LABEL[Enums.ChartTypeEnum.WEEKLY_VIEWS]
        ? await this.fetchListingChartData(params)
        : await this.fetchMarketChartData(chartName, params);

      this.chartData = ChartsFactory
        .getChartByType(chartData, this.chartWidth);
      this.chartSeries = chartData.charts;
      this.loadingGraph = false;
    },

    async fetchListingChartData(params) {
      const chartData = await DashboardApiService.getListingViewsByListingId(params.listingId);

      return { ...chartData, chartType: Enums.ChartTypeEnum.WEEKLY_VIEWS };
    },

    async fetchMarketChartData(chartName, requestFilter) {
      const { closedAt, ...filters } = FilterMapper.mapToDto(requestFilter);
      const chartType = Object.entries(CHART_LABEL)
        // eslint-disable-next-line no-unused-vars
        .find(([_, label]) => chartName === label)?.[0];
      const timeStampKey = chartType === Enums.ChartTypeEnum.NUMBER_OF_NEW_LISTINGS
        ? DtoKeys.SearchListingFilterDtoKey.PUBLISHED_AT
        : DtoKeys.SearchListingFilterDtoKey.CLOSED_AT;
      const chartRequestBody = {
        filters: {
          ...filters,
          [timeStampKey]: closedAt,
        },
        chartType,
      };
      const chartData = await MarketDataApiService.getChartData(chartRequestBody);

      return { ...chartData, chartType };
    },

    /**
     * Builds an image fromt he current graph and
     * forces the download of that image
     *
     * @param {String} graphName => the name of the exported graph
     */

    async downloadGraphImage(graphName) {
      const { imgURI } = await this.$refs.marketDataChart.dataURI();

      // Create new image with the selected graph
      // And appened the graph title and footer to the final image.
      const img = new Image();
      img.src = imgURI;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width + 20;
        canvas.height = img.height + 160;
        ctx.fillStyle = '#FFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#000';
        ctx.font = '19pt sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(graphName, canvas.width / 2, 50);
        ctx.drawImage(img, 10, 60);
        ctx.font = '16pt sans-serif';
        ctx.fillText(
          'Visit bode.ca/market-data to research home trends in your neighborhood.',
          canvas.width / 2,
          canvas.height - 40,
        );
        this.forceFileDownload(canvas.toDataURL('image/jpeg'), graphName);
      };
    },

    /**
     * This forces download of an image file given in
     * base64 dataURL format
     *
     * @param {String} data the graph image to force download of
     * @param {String} chartType the type of graph represented by the image
     * @private
     */

    forceFileDownload(data, chartType) {
      const link = document.createElement('a');
      link.href = data;
      link.setAttribute('download', `${chartType} Graph.jpeg`);
      document.body.appendChild(link);
      link.click();
    },
  },
};
